import type { JsonSchemaType } from '@gmn-portal/backend';
import { useContract, useContractVersions } from '@/core/api';
import { useGlobalMetadata } from '@/core/api/global-metadata';
import type { BackendActivityItem } from '@/core/api/types';
import { useIntl } from '@/core/i18n/use-intl';
import { ActivityBody, ActivityFieldChange, ActivityHeader } from '@/shared/components/activity';
import { getFieldName } from '@/shared/utils';

import { CustomActivityTimeline } from './activity-timeline';
import { ResolvedFieldValue } from './resolved-field-value';
import type { ContractActivityProps } from './types';

export function ContractActivity({ contractId, groupKey }: ContractActivityProps) {
  const { t } = useIntl();
  const {
    data: activityData,
    isLoading: isLoadingActivities,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useContractVersions(contractId!, {
    disabled: !contractId,
    limit: 15,
  });

  const { data: contract } = useContract(contractId!);
  const { data: globalMetadata } = useGlobalMetadata();

  if (isLoadingActivities) {
    return <div className="text-center text-muted-foreground">{t('activity.loading')}</div>;
  }

  if (!activityData?.pages[0].docs.length) {
    return <div className="text-center text-muted-foreground">{t('activity.noActivity')}</div>;
  }

  // Flatten all pages of activities
  const allActivities = activityData.pages.flatMap((page) => page.docs);

  if (!allActivities.length) {
    return <div className="text-center text-muted-foreground">{t('activity.noActivity')}</div>;
  }

  // Filter activities to only show "created" event if there are no more pages to load
  const filteredActivities = allActivities.filter((activity: BackendActivityItem) => {
    if (activity.type === 'created') return !hasNextPage;
    return true;
  });

  if (!filteredActivities.length) {
    return <div className="text-center text-muted-foreground">{t('activity.noActivity')}</div>;
  }

  // Render activity based on its type
  const renderActivity = (activity: BackendActivityItem) => {
    const metadataSchemaOverride = activity.schema?.contractMetadataSchema as
      | JsonSchemaType
      | undefined;
    const dynamicSchemaOverride = activity.schema?.contractDynamicSchema as
      | JsonSchemaType
      | undefined;
    switch (activity.type) {
      case 'created':
        return (
          <ActivityHeader userId={activity.user} timestamp={activity.timestamp}>
            <span className="text-muted-foreground">{t('activity.contract.created')}</span>
          </ActivityHeader>
        );

      case 'updated': {
        const nodes = (activity.changes ?? [])
          .map((change, index) => {
            // Skip if field has been deleted or is not accessible
            const fieldName = getFieldName(change.field, contract, globalMetadata, t, groupKey, {
              metadataSchema: metadataSchemaOverride,
              dynamicSchema: dynamicSchemaOverride,
            });
            if (fieldName === null) return null;

            const isMetadata =
              change.field.startsWith('metadata.') || change.field.startsWith('dynamicMetadata.');

            return (
              <ActivityFieldChange
                key={index}
                prefix={isMetadata ? t('activity.metadataField') : undefined}
                label={fieldName}
                from={
                  <ResolvedFieldValue
                    field={change.field}
                    value={change.from}
                    contract={contract}
                    metadataSchemaOverride={metadataSchemaOverride}
                    dynamicSchemaOverride={dynamicSchemaOverride}
                  />
                }
                to={
                  <ResolvedFieldValue
                    field={change.field}
                    value={change.to}
                    contract={contract}
                    metadataSchemaOverride={metadataSchemaOverride}
                    dynamicSchemaOverride={dynamicSchemaOverride}
                  />
                }
              />
            );
          })
          .filter(Boolean);

        return (
          <div>
            <ActivityHeader userId={activity.user} timestamp={activity.timestamp} />
            {nodes.length > 0 && <ActivityBody>{nodes}</ActivityBody>}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      <CustomActivityTimeline
        activities={filteredActivities}
        renderActivity={renderActivity}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
      />
    </div>
  );
}
